/**
 * NoticeCard — inline permanent alert / info card.
 *
 * NOT a transient toast (use `<Snackbar>` for those). NoticeCard sits
 * inline in the page flow, communicates state about the data or the
 * page, and usually carries an inline CTA to resolve the situation.
 *
 * Observed examples:
 *   - "Чтобы создавать проекты, обратитесь в поддержку" → tone `alert`
 *     + CTA «Восстановить права».
 *   - "Яндекс Метрика не подключена" → tone `bad`, CTA «Подключить».
 *   - Informational hints with light primary tone.
 *
 * Anatomy:
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ [icon] Title                                  [Action]   │
 *   │        Description                                        │
 *   └──────────────────────────────────────────────────────────┘
 *
 * The tone (`good` / `alert` / `bad` / `primary`) colors the left
 * accent border and the icon. The card itself uses a soft surface fill
 * tinted by the tone so it stands out without overpowering page chrome.
 *
 * @format
 */

import {forwardRef, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Icon} from 'ui/components/atoms/Icon';
import {text} from 'ui/theme/typography';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';

export const NOTICE_CARD_TONES = ['primary', 'good', 'alert', 'bad'] as const;
export type NoticeCardTone = (typeof NOTICE_CARD_TONES)[number];

export interface NoticeCardProps {
  /** Semantic tone of the notice. Drives icon color + border accent. */
  tone?: NoticeCardTone;
  /** Optional Material Symbols icon name. If omitted, the tone's default is used. */
  icon?: string | ReactNode | null;
  /** Notice title. */
  title?: ReactNode;
  /** Notice description (1–3 lines). */
  description?: ReactNode;
  /** Right-side inline CTA (Button, Link, anything). */
  action?: ReactNode;
  className?: string;
}

const DEFAULT_ICON: Record<NoticeCardTone, string> = {
  primary: 'info',
  good: 'check_circle',
  alert: 'warning',
  bad: 'error',
};

const Root = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: NoticeCardTone}>(({theme, $tone}) => {
  const color = theme.palette[$tone].main;
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
    padding: theme.spacing(3.5, 4),
    borderRadius: 12,
    border: `1px solid ${color}`,
    backgroundColor: `${color}14`,
    color: theme.palette.surface.onSurface,
  };
});

const IconSlot = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: NoticeCardTone}>(({theme, $tone}) => ({
  color: theme.palette[$tone].main,
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  marginTop: theme.spacing(0.5),
}));

const TextStack = styled(Stack)({
  flex: '1 1 auto',
  minWidth: 0,
});

const Title = styled(Box)(() => ({
  ...text('label.m'),
}));

// `body.s` (12) читался мельче нижней границы, принятой для текста
// продукта: карточка объясняет ситуацию словами, и это основной текст
// блока, а не сноска под ним.
const Description = styled(Box)(({theme}) => ({
  ...text('body.m'),
  color: theme.palette.surface.onSurfaceLight,
}));

const ActionSlot = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: '0 0 auto',
});

function renderIcon(icon: NoticeCardProps['icon'], fallback: string): ReactNode {
  if (icon === null) return null;
  if (icon === undefined) return <Icon name={fallback} size={20} color='inherit' />;
  if (typeof icon === 'string') return <Icon name={icon} size={20} color='inherit' />;
  return icon;
}

export const NoticeCard = forwardRef<HTMLDivElement, NoticeCardProps>(function NoticeCard(
  {tone = 'primary', icon, title, description, action, className},
  ref,
) {
  const iconNode = renderIcon(icon, DEFAULT_ICON[tone]);
  return (
    <Root ref={ref} $tone={tone} className={className}>
      {iconNode != null && <IconSlot $tone={tone}>{iconNode}</IconSlot>}
      <TextStack gap='xxs'>
        {title != null && <Title>{title}</Title>}
        {description != null && <Description>{description}</Description>}
      </TextStack>
      {action != null && <ActionSlot>{action}</ActionSlot>}
    </Root>
  );
});

// Suppress unused-Row warning when no row is used. Kept import to ensure
// Row primitive is loaded for future variants.
void Row;
